import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import {
	useFetchUserTraitsOld,
	useUserTraits,
} from '@atlassian/jira-business-common/src/common/providers/controllers/traits/index.tsx';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import {
	MiniBody,
	Mini,
} from '@atlassian/jira-minimizable-modal/src/ui/modal-container/mini/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { isPartOfDiscoverabilityTriggers } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/utils.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { usePersistentIssueCreateModalViewResource } from '@atlassian/jira-router-resources-persistent-issue-create-modal-view/src/controllers/main.tsx';
import { GIC_VARIANTS } from '../../../../common/ui/footer/constants.tsx';
import Footer from '../../../../common/ui/footer/index.tsx';
import { MiniHeader } from '../../../../common/ui/minimizable-modal-mini-header/index.tsx';
import { useParseSummaryValue } from '../../../../common/utils/parse-summary-value/index.tsx';
import { useMinimizableModalWrapperProps } from '../../../../controllers/minimizable-modal-wrapper-props/main.tsx';
import { SpotlightContainer } from './modal-spotlight/index.tsx';
import type { MinimizableModalMiniProps } from './types.tsx';

export const MinimizableModalMini = forwardRef<HTMLElement, MinimizableModalMiniProps>(
	({ children, summaryValue, ...rest }: MinimizableModalMiniProps, ref) => {
		const {
			onModalCloseWithConfirm,
			onSubmit,
			isSubmitting,
			isOperationInProgress,
			canCreateAnotherIssue,
			onCreateAnotherChange,
			getShouldDisableSubmit,
			createdIssues,
			displayCreateAnotherIssueToggle,
			selectedIssueType,
			projectType,
			dismissModalEntryPointWrapper,
		} = useMinimizableModalWrapperProps();

		const issueTypeRef = useRef(selectedIssueType);
		const { parseSummaryValue } = useParseSummaryValue();
		const [shouldRenderMiniModalSpotlight, setShouldRenderMiniModalSpotlight] =
			useState<boolean>(false);

		const [{ shouldShowMiniModalSpotlight, triggerPointKey }, { dismissModalSpotlight }] =
			useTriggerIssueCreateModal();

		const [userTraits, { setUserTraits }] = useUserTraits();
		const fetchUserTraits = useFetchUserTraitsOld();

		const setUserTraitsIfNotAvailable = useCallback(async () => {
			const newTraits = await fetchUserTraits();
			// We must explicitly check for false because fetchUserTraits returns "false" when pending, and "null" when traits are empty.
			if (newTraits !== false) {
				setUserTraits(newTraits);
			}
		}, [fetchUserTraits, setUserTraits]);

		const { updateIsDiscoPushComplete } = usePersistentIssueCreateModalViewResource();

		const { data: projectContext } = useProjectContext();

		const markMiniModalAsSeen = useCallback(() => {
			try {
				const shouldDismiss =
					isPartOfDiscoverabilityTriggers(triggerPointKey) &&
					projectContext?.projectType !== SERVICE_DESK_PROJECT;

				if (shouldDismiss) {
					updateIsDiscoPushComplete(true);
					dismissModalSpotlight();
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'setMiniModalSpotlightPreference',
						packageName: 'jiraIssueCreate',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		}, [
			dismissModalSpotlight,
			triggerPointKey,
			updateIsDiscoPushComplete,
			projectContext?.projectType,
		]);

		useEffect(
			() => () => {
				markMiniModalAsSeen();
			},
			[markMiniModalAsSeen],
		);

		useEffect(() => {
			if (selectedIssueType) {
				issueTypeRef.current = selectedIssueType;
			}
		}, [selectedIssueType]);

		useEffect(() => {
			if (!userTraits?.attributes?.length && userTraits?.attributes !== null) {
				setUserTraitsIfNotAvailable();
			} else {
				const accountCreationDate = userTraits?.attributes?.find(
					(trait) => trait?.name === 'jira_first_active',
				)?.value;
				const aWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
				let isNewUser = false;
				if (accountCreationDate && typeof accountCreationDate === 'string') {
					isNewUser = new Date(accountCreationDate) > aWeekAgo;
				}

				setShouldRenderMiniModalSpotlight(!!shouldShowMiniModalSpotlight && !isNewUser);
			}
		}, [userTraits, shouldShowMiniModalSpotlight, setUserTraitsIfNotAvailable]);

		return (
			// @ts-expect-error - TS2746 - This JSX tag's 'children' prop expects a single child of type 'HTMLCollection', but multiple children were provided.
			<Mini ref={ref} {...rest}>
				{shouldRenderMiniModalSpotlight && (
					<SpotlightContainer
						shouldShowSpotlight={shouldRenderMiniModalSpotlight}
						onCloseSpotlight={markMiniModalAsSeen}
					/>
				)}
				<MiniHeader
					title={parseSummaryValue(summaryValue, issueTypeRef.current?.label)}
					isItalic={!summaryValue}
					iconUrl={issueTypeRef.current?.iconUrl}
					showHeaderIcon={projectType !== SERVICE_DESK_PROJECT}
					dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
				/>
				<MiniBody>{children}</MiniBody>
				<Footer
					onClose={onModalCloseWithConfirm('cancel')}
					onSubmit={onSubmit}
					isSubmitting={isSubmitting}
					isOperationInProgress={isOperationInProgress}
					canCreateAnotherIssue={canCreateAnotherIssue}
					onCreateAnotherChange={onCreateAnotherChange}
					shouldDisableSubmit={getShouldDisableSubmit()}
					createdIssues={createdIssues}
					variant={GIC_VARIANTS.MINI}
					displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
				/>
			</Mini>
		);
	},
);
